import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  Select,
  Stack,
  Tooltip,
  Typography,
  Button,
  Dialog,
} from "@mui/material";
import { FieldArray, FormikProvider, useFormik } from "formik";
import * as yup from "yup";
import {
  StyledTextField,
} from "../../../../../../customComponents/styled/styledInputs";
import { FilledButton } from "../../../../../../customComponents/styled/styledButtons";
import {
  AddCircle,
  Check,
  TaskAlt,
  RemoveCircle,
} from "@mui/icons-material";
import { useState, useMemo, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import CustomTipTapEditor from "../../../../../../customComponents/CustomTipTapEditor";
import {
  useCreateQuestionMutation,
  useEditQuestionMutation,
  useGetTeacherSubjectsQuery,
} from "../../../../../../app/questionApiSlice";
import toast from "../../../../../../helpers/toast";

const ObjectiveForm = ({ initialData, isEditing = false, questionId }) => {
  const [openSuccess, setOpenSuccess] = useState(false);
  const navigate = useNavigate();
  const [createQuestion, { isLoading: isCreatingQuestion }] =
    useCreateQuestionMutation();
  const [editQuestion, { isLoading: isEditingQuestion }] =
    useEditQuestionMutation();

  const { data: classrooms, isLoading: isFetchingClassrooms } =
    useGetTeacherSubjectsQuery();

  // Get unique subjects the teacher teaches
  const availableSubjects = useMemo(() => {
    if (!classrooms) return [];
    const subjectMap = new Map();
    classrooms.forEach((subject) => {
      if (!subjectMap.has(subject.name)) {
        subjectMap.set(subject.name, subject);
      }
    });
    return Array.from(subjectMap.values());
  }, [classrooms]);

  const formik = useFormik({
    initialValues: {
      subject: initialData?.subject || "",
      level: initialData?.level || "",
      topic: initialData?.topic || "",
      body: initialData?.body || "",
      options: initialData?.options && initialData.options.length > 0
        ? initialData.options.map((opt) => ({ ...(opt.id ? { id: opt.id } : {}), body: opt.body, is_answer: opt.is_answer }))
        : [
            { body: "", is_answer: true },
            { body: "", is_answer: false },
          ],
      explanation: initialData?.explanation || "",
    },
    validationSchema: yup.object({
      subject: yup.string().required("Required"),
      level: yup.string().required("Required"),
      topic: yup.string().required("Required"),
      body: yup.string().required("Required"),
      options: yup.array().required("Required"),
      explanation: yup.string(),
    }),
    onSubmit: async (values) => {
      try {
        if (isEditing) {
          await editQuestion({ values, questionId }).unwrap();
          toast.success("Question updated");
        } else {
          await createQuestion(values).unwrap();
          toast.success("Question created");
        }
        setOpenSuccess(true);
      } catch (error) {
        console.error(error);
        const msg = error?.data?.message || error?.message || "Failed to save question. Please try again.";
        toast.error(msg);
      }
    },
  });

  const submitRef = useRef(null);
  useEffect(() => { submitRef.current = formik.handleSubmit; });
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        submitRef.current?.();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Get levels that teach the selected subject
  const availableLevels = useMemo(() => {
    if (!formik.values.subject || !classrooms) return [];
    const levelMap = new Map();
    classrooms.forEach((subject) => {
      if (subject.name === formik.values.subject) {
        if (!levelMap.has(subject.level)) {
          levelMap.set(subject.level, subject.level);
        }
      }
    });
    return Array.from(levelMap.values());
  }, [formik.values.subject, classrooms]);

  const StepBadge = ({ number }) => (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, borderRadius: "50%", backgroundColor: "primary.main", color: "primary.contrastText", fontSize: "14px", fontWeight: 600 }}>
      {number}
    </Box>
  );

  return (
    <Box component="form" onSubmit={formik.handleSubmit} sx={{ width: "100%" }}>
      {/* Success Dialog */}
      <Dialog
        fullWidth
        open={openSuccess}
        onClose={() => setOpenSuccess(false)}
        sx={{ "& .MuiPaper-root": { borderRadius: "24px", maxWidth: "776px" } }}
      >
        <Stack sx={{ alignItems: "center", gap: 2.5, py: 5, px: 3 }}>
          <TaskAlt sx={{ width: "136px", height: "162px", color: "primary.main" }} />
          <Typography sx={{ color: "text.primary", fontSize: "20px", fontWeight: 600 }}>
            {isEditing ? "Question Updated" : "Question Created"}
          </Typography>
          <Typography sx={{ color: "#95969D", fontSize: "16px", textAlign: "center" }}>
            Your question has been {isEditing ? "updated" : "created"} successfully
          </Typography>
          {!isEditing ? (
            <Stack direction="row" spacing={2} sx={{ width: "100%", justifyContent: "center" }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setOpenSuccess(false);
                  navigate(-1);
                }}
                sx={{ minWidth: "150px", fontWeight: 500, fontSize: "14px" }}
              >
                BACK TO QUESTIONS
              </Button>
              <FilledButton
                onClick={() => {
                  setOpenSuccess(false);
                  formik.resetForm({
                    values: {
                      subject: formik.values.subject,
                      level: formik.values.level,
                      topic: formik.values.topic,
                      body: "",
                      options: [
                        { body: "", is_answer: true },
                        { body: "", is_answer: false },
                      ],
                      explanation: "",
                    },
                  });
                  setTimeout(() => {
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }, 0);
                }}
                sx={{ minWidth: "150px", fontWeight: 500, fontSize: "14px" }}
              >
                ADD ANOTHER
              </FilledButton>
            </Stack>
          ) : (
            <FilledButton
              onClick={() => setOpenSuccess(false)}
              sx={{ width: "240px", fontWeight: 400, fontSize: "14px" }}
            >
              CONTINUE EDITING
            </FilledButton>
          )}
        </Stack>
      </Dialog>

      {/* Step 1: Subject, Level & Topic */}
      <Card sx={{ mb: 4, border: (theme) => `1px solid ${theme.palette.mode === "dark" ? "#444" : "#E0E0E0"}` }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
            <StepBadge number={1} />
            Subject, Level & Topic
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Subject</InputLabel>
                <Select
                  name="subject"
                  value={formik.values.subject}
                  onChange={(e) => {
                    formik.setFieldValue("subject", e.target.value);
                    formik.setFieldValue("level", "");
                  }}
                  label="Subject"
                  disabled={isFetchingClassrooms}
                  sx={{ backgroundColor: (theme) => theme.palette.mode === "dark" ? "#2a2a2a" : "#F9F9F9", borderRadius: "12px" }}
                >
                  <MenuItem value="">
                    <em>Select a subject</em>
                  </MenuItem>
                  {availableSubjects?.map((subject) => (
                    <MenuItem value={subject.name} key={subject.id}>
                      {subject.name}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.subject && formik.errors.subject && (
                  <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                    {formik.errors.subject}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Level</InputLabel>
                <Select
                  name="level"
                  value={formik.values.level}
                  onChange={formik.handleChange}
                  label="Level"
                  disabled={!formik.values.subject}
                  sx={{ backgroundColor: (theme) => theme.palette.mode === "dark" ? "#2a2a2a" : "#F9F9F9", borderRadius: "12px", textTransform: "capitalize" }}
                >
                  <MenuItem value="">
                    <em>{formik.values.subject ? "Select a level" : "Select subject first"}</em>
                  </MenuItem>
                  {availableLevels?.map((level) => (
                    <MenuItem value={level} key={level} sx={{ textTransform: "capitalize" }}>
                      {level}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.level && formik.errors.level && (
                  <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                    {formik.errors.level}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <StyledTextField
                  name="topic"
                  value={formik.values.topic}
                  onChange={formik.handleChange}
                  placeholder="Enter topic"
                  label="Topic"
                  InputLabelProps={{ shrink: true }}
                />
                {formik.touched.topic && formik.errors.topic && (
                  <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                    {formik.errors.topic}
                  </Typography>
                )}
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Step 2: Question */}
      <Card sx={{ mb: 4, border: (theme) => `1px solid ${theme.palette.mode === "dark" ? "#444" : "#E0E0E0"}` }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
            <StepBadge number={2} />
            Question
          </Typography>
          <CustomTipTapEditor
            placeholder="Write your question here..."
            value={formik.values.body}
            onChange={(markdown) => formik.setFieldValue("body", markdown)}
            showToolbar={true}
            minHeight="200px"
          />
          {formik.touched.body && formik.errors.body && (
            <Typography color="error" variant="caption" sx={{ mt: 1, display: "block" }}>
              {formik.errors.body}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Step 3: Options */}
      <Card sx={{ mb: 4, border: (theme) => `1px solid ${theme.palette.mode === "dark" ? "#444" : "#E0E0E0"}` }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
            <StepBadge number={3} />
            Options
          </Typography>
          <FormikProvider value={formik}>
            <FieldArray
              name="options"
              render={(arrayHelpers) => {
                const options = formik.values.options;
                const correctAnswerIndex = options.findIndex(opt => opt.is_answer);
                return (
                  <Stack spacing={3}>
                    {options.map((body, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          gap: 2,
                          alignItems: "flex-start",
                          p: 2.5,
                          border: body.is_answer ? "2px solid" : (theme) => `1px solid ${theme.palette.mode === "dark" ? "#444" : "#E0E0E0"}`,
                          borderColor: body.is_answer ? "primary.main" : undefined,
                          borderRadius: "12px",
                          backgroundColor: body.is_answer ? (theme) => theme.palette.mode === "dark" ? "#1a2a3a" : "#f0f7ff" : (theme) => theme.palette.mode === "dark" ? "#1e1e1e" : "#fff",
                        }}
                      >
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, pt: 1 }}>
                          <Radio
                            checked={index === correctAnswerIndex}
                            onChange={() => {
                              options.forEach((_, i) => {
                                formik.setFieldValue(`options.${i}.is_answer`, i === index);
                              });
                            }}
                            name="correct-answer"
                            inputProps={{ "aria-label": `Option ${index + 1} is correct` }}
                          />
                          <Typography variant="caption" sx={{ color: body.is_answer ? "primary.main" : "#666", fontWeight: body.is_answer ? 600 : 400 }}>
                            {body.is_answer ? "Correct" : "Correct?"}
                          </Typography>
                        </Box>
                        <Box sx={{ flex: 1, width: "100%" }}>
                          <Box sx={{ display: "flex", gap: 1, mb: 1.5, alignItems: "center" }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              Option {index + 1}
                            </Typography>
                            {index === 0 ? (
                              <Tooltip title="Add another option" placement="top">
                                <span>
                                  <IconButton
                                    onClick={() => arrayHelpers.push({ body: "", is_answer: false })}
                                    disabled={options.length >= 4}
                                    size="small"
                                  >
                                    <AddCircle sx={{ color: "primary.main" }} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Remove this option" placement="top">
                                <span>
                                  <IconButton
                                    onClick={() => arrayHelpers.remove(index)}
                                    disabled={options.length <= 2}
                                    size="small"
                                  >
                                    <RemoveCircle sx={{ color: "primary.main" }} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </Box>
                          <CustomTipTapEditor
                            placeholder="Write your option here..."
                            value={body.body}
                            onChange={(html) => formik.setFieldValue(`options.${index}.body`, html)}
                            showToolbar={true}
                            minHeight="200px"
                          />
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                );
              }}
            />
          </FormikProvider>
        </CardContent>
      </Card>

      {/* Step 4: Explanation */}
      <Card sx={{ mb: 4, border: (theme) => `1px solid ${theme.palette.mode === "dark" ? "#444" : "#E0E0E0"}` }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
            <StepBadge number={4} />
            Explanation
          </Typography>
          <CustomTipTapEditor
            value={formik.values.explanation}
            onChange={(value) => formik.setFieldValue("explanation", value)}
            placeholder="Write your explanation here..."
            showToolbar={true}
            minHeight="200px"
          />
          {formik.touched.explanation && formik.errors.explanation && (
            <Typography color="error" variant="caption" sx={{ mt: 1, display: "block" }}>
              {formik.errors.explanation}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Submit */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="center" sx={{ mt: 6, mb: 4 }}>
        <Button variant="outlined" onClick={() => navigate(-1)} sx={{ minWidth: 150 }}>
          Cancel
        </Button>
        <FilledButton
          type="submit"
          disabled={isCreatingQuestion || isEditingQuestion}
          sx={{ minWidth: 200, fontWeight: 500, fontSize: "16px" }}
        >
          {isCreatingQuestion || isEditingQuestion ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              {isEditing ? "Updating..." : "Creating..."}
            </>
          ) : (
            <>
              <Check sx={{ mr: 1 }} />
              {isEditing ? "Update Question" : "Create Question"}
            </>
          )}
        </FilledButton>
      </Stack>
    </Box>
  );
};

export default ObjectiveForm;
